import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, Trash2, Eye, User, Clock, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommentManager = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedComments, setSelectedComments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/comments/admin/comments`, {
        params: { status: statusFilter === 'all' ? null : statusFilter },
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
      setPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [statusFilter]);

  const handleAction = async (commentId, action) => {
    try {
      const token = localStorage.getItem('admin_token');
      if (action === 'delete') {
        await axios.delete(`${API}/comments/admin/comments/${commentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/comments/admin/comments/${commentId}`, 
          { status: action === 'approve' ? 'approved' : 'rejected' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchComments();
    } catch (error) {
      console.error(`Error ${action}ing comment:`, error);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedComments.length === 0) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/comments/admin/comments/bulk-action`, 
        { action, comment_ids: selectedComments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedComments([]);
      fetchComments();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const toggleSelect = (commentId) => {
    setSelectedComments(prev => 
      prev.includes(commentId) 
        ? prev.filter(id => id !== commentId)
        : [...prev, commentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedComments.length === comments.length) {
      setSelectedComments([]);
    } else {
      setSelectedComments(comments.map(c => c.id));
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status}
      </span>
    );
  };

  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-orange-400" />
            Comment Moderation
            {pendingCount > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchComments} className="border-gray-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters and Bulk Actions */}
        <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-gray-800 border-gray-700">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Comments</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedComments.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">{selectedComments.length} selected</span>
              <Button size="sm" onClick={() => handleBulkAction('approve')} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button size="sm" onClick={() => handleBulkAction('reject')} className="bg-yellow-600 hover:bg-yellow-700">
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')}>
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No {statusFilter !== 'all' ? statusFilter : ''} comments found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All */}
            <div className="flex items-center gap-2 pb-2 border-b border-gray-800">
              <Checkbox 
                checked={selectedComments.length === comments.length && comments.length > 0}
                onCheckedChange={toggleSelectAll}
                className="border-gray-600"
              />
              <span className="text-sm text-gray-400">Select All</span>
            </div>

            {comments.map((comment) => (
              <div 
                key={comment.id} 
                className={`p-4 rounded-lg border transition-colors ${
                  selectedComments.includes(comment.id) 
                    ? 'bg-gray-800/80 border-orange-500/50' 
                    : 'bg-gray-800/50 border-gray-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox 
                    checked={selectedComments.includes(comment.id)}
                    onCheckedChange={() => toggleSelect(comment.id)}
                    className="mt-1 border-gray-600"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-white">{comment.author_name}</span>
                      </div>
                      <span className="text-gray-500 text-sm">{comment.author_email}</span>
                      {getStatusBadge(comment.status)}
                    </div>
                    
                    <p className="text-gray-300 mb-3">{comment.content}</p>
                    
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(comment.created_at)}
                        </span>
                        <span>Post ID: {comment.post_id}</span>
                        {comment.parent_id && (
                          <span className="text-orange-400">↳ Reply</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {comment.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => handleAction(comment.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700 h-8"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleAction(comment.id, 'reject')}
                              className="bg-yellow-600 hover:bg-yellow-700 h-8"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {comment.status === 'rejected' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleAction(comment.id, 'approve')}
                            className="bg-green-600 hover:bg-green-700 h-8"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                        )}
                        {comment.status === 'approved' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleAction(comment.id, 'reject')}
                            variant="outline"
                            className="border-gray-700 h-8"
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleAction(comment.id, 'delete')}
                          className="h-8"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommentManager;
